import { useCallback, useEffect, useRef, useState } from 'react';
import './ScoreCardSliderField.scss';

export type ScoreCardSliderOption = { label: string; value: string | number };

export type ScoreCardSliderFieldProps = {
    title: string;
    description?: string | null;
    options: ScoreCardSliderOption[];
    value: string | number | null | undefined;
    onSelect: (value: string | number) => void;
    error?: boolean;
    required?: boolean;
};

type ColPosition = 'first' | 'middle' | 'last' | 'only';

const MOBILE_SLIDER_MQ = '(max-width: 899px)';

function useIsMobileScoreSlider(): boolean {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(MOBILE_SLIDER_MQ).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_SLIDER_MQ);
        const sync = () => setIsMobile(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    return isMobile;
}

function optionIndex(options: ScoreCardSliderOption[], raw: string | number | null | undefined) {
    if (raw === '' || raw == null) return -1;
    return options.findIndex((o) => String(o.value) === String(raw));
}

function sliderRatio(selectedIndex: number, optionCount: number): number {
    if (optionCount <= 1) return 0;
    if (selectedIndex < 0) return 0;
    return selectedIndex / (optionCount - 1);
}

type MobileDiscreteSliderProps = {
    options: ScoreCardSliderOption[];
    selectedIndex: number;
    title: string;
    onSelect: (value: string | number) => void;
};

function MobileDiscreteSlider({ options, selectedIndex, title, onSelect }: MobileDiscreteSliderProps) {
    const n = options.length;
    const innerRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);
    const dragRRef = useRef(0);
    const [dragR, setDragR] = useState<number | null>(null);

    const ratioFromClientX = useCallback(
        (clientX: number) => {
            const el = innerRef.current;
            if (!el || n < 1) return 0;
            const rect = el.getBoundingClientRect();
            if (rect.width <= 0) return 0;
            const t = (clientX - rect.left) / rect.width;
            return Math.min(1, Math.max(0, t));
        },
        [n]
    );

    const indexFromRatio = (r: number) => (n === 1 ? 0 : Math.round(r * (n - 1)));

    const syncDragVisual = (r: number) => {
        dragRRef.current = r;
        setDragR(r);
    };

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        draggingRef.current = true;
        const r = ratioFromClientX(e.clientX);
        syncDragVisual(r);
        e.currentTarget.setPointerCapture(e.pointerId);
        if (n > 1) {
            const idx = indexFromRatio(r);
            if (idx !== selectedIndex) {
                onSelect(options[idx].value);
            }
        }
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current) return;
        const r = ratioFromClientX(e.clientX);
        syncDragVisual(r);
        if (n > 1) {
            const idx = indexFromRatio(r);
            if (idx !== selectedIndex) {
                onSelect(options[idx].value);
            }
        }
    };

    const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
        if (draggingRef.current && n > 1) {
            const idx = indexFromRatio(dragRRef.current);
            if (idx !== selectedIndex) {
                onSelect(options[idx].value);
            }
        }
        draggingRef.current = false;
        setDragR(null);
        try {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
        } catch {
            /* releasePointerCapture can throw if already released */
        }
    };

    const committedRatio = sliderRatio(selectedIndex, n);
    const displayRatio = dragR !== null ? dragR : committedRatio;

    return (
        <div
            className="hive-score-slider__mobile-track"
            role="group"
            aria-label={title}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
        >
            <div
                ref={innerRef}
                className="hive-score-slider__mobile-inner"
                style={{
                    ['--hive-slider-r' as string]: String(n <= 1 ? 0 : displayRatio)
                }}
            >
                <div className="hive-score-slider__mobile-rail" aria-hidden>
                    <div className="hive-score-slider__mobile-rail-bg" />
                    <div
                        className={[
                            'hive-score-slider__mobile-rail-fill',
                            n <= 1 ? 'hive-score-slider__mobile-rail-fill--single' : '',
                            selectedIndex < 0 ? 'hive-score-slider__mobile-rail-fill--empty' : ''
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    />
                </div>
                <button
                    type="button"
                    className={`hive-score-slider__mobile-thumb ${n <= 1 ? 'hive-score-slider__mobile-thumb--only' : ''}`}
                    aria-label={
                        selectedIndex >= 0 ? options[selectedIndex].label : 'Select value'
                    }
                    aria-valuenow={selectedIndex >= 0 ? selectedIndex + 1 : 1}
                    aria-valuetext={
                        selectedIndex >= 0 ? options[selectedIndex].label : 'Not selected'
                    }
                    aria-valuemin={1}
                    aria-valuemax={n}
                    role="slider"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (n < 2) return;
                        let next = selectedIndex >= 0 ? selectedIndex : 0;
                        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            next = Math.max(0, next - 1);
                            onSelect(options[next].value);
                        } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            next = Math.min(n - 1, next + 1);
                            onSelect(options[next].value);
                        }
                    }}
                />
                <div className="hive-score-slider__mobile-dots" aria-hidden>
                    {options.map((opt, i) => (
                        <span key={`${opt.value}-${i}`} className="hive-score-slider__mobile-dot" />
                    ))}
                </div>
            </div>
        </div>
    );
}

export function ScoreCardSliderField({
    title,
    description,
    options,
    value,
    onSelect,
    error,
    required
}: ScoreCardSliderFieldProps) {
    const isMobile = useIsMobileScoreSlider();
    const n = options.length;
    const selectedIndex = optionIndex(options, value);

    if (n === 0) {
        return null;
    }

    const colClass = (i: number): ColPosition => {
        if (n === 1) return 'only';
        if (i === 0) return 'first';
        if (i === n - 1) return 'last';
        return 'middle';
    };

    const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

    return (
        <div className={`hive-score-slider-field ${error ? 'hive-score-slider-field--error' : ''}`}>
            <h3 className="hive-score-slider-field__title">
                {title}
                {required ? <span className="red"> *</span> : null}
            </h3>

            {isMobile ? (
                <div className="hive-score-slider__mobile-scale">
                    {selectedOption ? (
                        <p className="hive-score-slider-field__value-preview" aria-live="polite">
                            {selectedOption.label}
                        </p>
                    ) : null}
                    <MobileDiscreteSlider
                        options={options}
                        selectedIndex={selectedIndex}
                        title={title}
                        onSelect={onSelect}
                    />
                </div>
            ) : (
                <>
                    {selectedOption ? (
                        <p className="hive-score-slider-field__value-preview" aria-live="polite">
                            {selectedOption.label}
                        </p>
                    ) : null}
                    <div
                        className="hive-score-slider__track hive-score-slider__track--desktop"
                        role="radiogroup"
                        aria-label={title}
                    >
                    {options.map((option, i) => {
                        const pos = colClass(i);
                        const selected = selectedIndex === i;
                        const lineRightActive = selectedIndex > i;
                        const lineLeftActive = selectedIndex > i - 1;

                        return (
                            <div
                                key={`${option.value}-${i}`}
                                className={`hive-score-slider__col hive-score-slider__col--${pos}`}
                            >
                                {pos === 'middle' ? (
                                    <div
                                        className={`hive-score-slider__line hive-score-slider__line--lead ${
                                            lineLeftActive ? 'hive-score-slider__line--active' : ''
                                        }`}
                                        aria-hidden
                                    />
                                ) : null}
                                {pos === 'last' ? (
                                    <div
                                        className={`hive-score-slider__line ${
                                            lineLeftActive ? 'hive-score-slider__line--active' : ''
                                        }`}
                                        aria-hidden
                                    />
                                ) : null}

                                <button
                                    type="button"
                                    className={`hive-score-slider__radio ${
                                        selected ? 'hive-score-slider__radio--selected' : ''
                                    }`}
                                    aria-checked={selected}
                                    role="radio"
                                    aria-label={option.label}
                                    onClick={() => onSelect(option.value)}
                                />

                                {pos === 'first' || pos === 'middle' ? (
                                    <div
                                        className={`hive-score-slider__line ${
                                            pos === 'middle' ? 'hive-score-slider__line--trail' : ''
                                        } ${lineRightActive ? 'hive-score-slider__line--active' : ''}`}
                                        aria-hidden
                                    />
                                ) : null}

                                <p className="hive-score-slider__label">{option.label}</p>
                            </div>
                        );
                    })}
                    </div>
                </>
            )}

            {description ? <p className="hive-score-slider-field__hint">{description}</p> : null}
            {error ? (
                <div className="hive-score-slider-field__error">
                    This question is required. Please select one of the options
                </div>
            ) : null}
        </div>
    );
}
